import { Button } from 'components/atoms';
import { Form } from 'components/organisms';
import {
  musicProductPhysicalInitialValues,
  musicProductPhysicalTabs,
} from 'components/organisms/forms/music-product-physical/MusicProductPhysicalForm';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import { PiFloppyDisk, PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useGetMusicProductPhysical,
  usePutMusicProductPhysical,
} from 'services/music-product-physical';
import {
  useDeleteMusicProductPhysicalImage,
  usePostMusicProductPhysicalImage,
  usePutMusicProductPhysicalImage,
} from 'services/music-product-physical/image';
import { usePostMusicProductPhysicalSettlementInfo } from 'services/music-product-physical/settlementInfo';
import { IMusicProductPhysicalForm } from 'services/music-product-physical/types/form';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getLastTabPathname } from 'utils/navbar/getParentPathname';
import { onErrorShowToast } from 'utils/service/errorHandler';

const EditMusicProductPhysical = () => {
  const physicalMusicProductId = useParamId('music-product-physical');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const { data: musicProductPhysical, isLoading } = useGetMusicProductPhysical(
    physicalMusicProductId,
  );
  const updateMusicProductPhysical = usePutMusicProductPhysical();

  const updateSettlementInfo = usePostMusicProductPhysicalSettlementInfo();

  const addImage = usePostMusicProductPhysicalImage();
  const changeImage = usePutMusicProductPhysicalImage();
  const deleteImage = useDeleteMusicProductPhysicalImage(
    physicalMusicProductId,
  );

  const mutateOptions = useMutateOptions<IMusicProductPhysicalForm>();

  const musicProductPhysicalEditInitialValues = musicProductPhysical
    ? middleware.musicProductPhysical.queryToForm(musicProductPhysical)
    : musicProductPhysicalInitialValues;

  const onEditMusicProductPhysical = async (
    values: IMusicProductPhysicalForm,
    formikHelpers: FormikHelpers<IMusicProductPhysicalForm>,
  ) => {
    let productVersion = values.version;
    if (values.file) {
      musicProductPhysicalEditInitialValues.productImageLink?.length
        ? await changeImage.mutateAsync(
            {
              file: values.file,
              id: physicalMusicProductId,
              version: values.version,
            },
            {
              onError: error => {
                onErrorShowToast(error, 'toast-message.change-album-cover');
              },
              onSuccess: data => {
                productVersion = data.version;
              },
            },
          )
        : await addImage.mutateAsync(
            {
              file: values.file,
              id: physicalMusicProductId,
              version: values.version,
            },
            {
              onError: error => {
                onErrorShowToast(error, 'toast-message.add-album-cover');
              },
              onSuccess: data => {
                productVersion = data.version;
                formikHelpers.setFieldValue(
                  'productImageLink',
                  data.productImageLink,
                );
              },
            },
          );
    }

    if (
      !!musicProductPhysicalEditInitialValues.productImageLink?.length &&
      !values.productImageLink?.length &&
      !values.file
    ) {
      await deleteImage.mutateAsync(undefined, {
        onError: error => {
          onErrorShowToast(error, 'toast-message.remove-album-cover');
        },
        onSuccess: data => {
          productVersion = data.version;
        },
      });
    }

    const musicProductPhysicalMutation =
      middleware.musicProductPhysical.formToQuery(values);

    await updateMusicProductPhysical.mutateAsync(
      {
        ...musicProductPhysicalMutation,
        physicalMusicProductId,
        version: productVersion,
      },
      {
        ...mutateOptions(formikHelpers),
        onSuccess: goToIndividualMusicProductPhysical,
      },
    );
  };

  const goToIndividualMusicProductPhysical = () =>
    navigate(
      `/music-product-physical/${physicalMusicProductId}/${getLastTabPathname(
        pathname,
        4,
      )}`,
    );

  return (
    <Form.MusicProductPhysical
      i18nextPrefix='music-product-physical'
      onSubmit={onEditMusicProductPhysical}
      initialValues={musicProductPhysicalEditInitialValues}
      disableTabs={[musicProductPhysicalTabs.statistics]}
      status={{ isLoading }}
      customBackItem={
        <Button
          type='button'
          onlyIcon
          onClick={goToIndividualMusicProductPhysical}>
          <Button.Description description={t('button.cancel')} />

          <Button.Icon icon={<PiX />} />
        </Button>
      }
      topbarContent={
        <div className='flex items-center gap-2'>
          <Button type='submit' variant={BUTTON_VARIANT.submit}>
            <Button.Text text={t('button.save')} />
            <Button.Icon icon={<PiFloppyDisk />} />
          </Button>
        </div>
      }
    />
  );
};

export default EditMusicProductPhysical;
